import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL as configApiUrl } from '../config/api';

// Persist language preference to backend using BOTH Bearer token (native /
// localStorage flow) AND credentials cookie (web flow). Either auth path is
// enough; we don't fail if one of them is missing. This fixes the bug where
// users authenticated only via cookie (e.g. web/Google OAuth) had their
// language change saved locally but never reach `users.language` in the DB,
// causing the Admin Support tab to keep grouping them under the old language.
async function persistLanguageToBackend(lang) {
  try {
    const token = localStorage.getItem('session_token') || localStorage.getItem('access_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    await axios.put(
      `${configApiUrl}/api/user/language`,
      { language: lang },
      { headers, withCredentials: true },
    );
    return true;
  } catch (e) {
    return false;
  }
}

// Import all translations
import it from '../locales/it.json';
import en from '../locales/en.json';
import es from '../locales/es.json';
import pt from '../locales/pt.json';
import de from '../locales/de.json';
import fr from '../locales/fr.json';

const translations = { it, en, es, pt, de, fr };

export const LANGUAGES = [
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'pt', name: 'Português', flag: '🇧🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
];

// Map country codes to languages
const COUNTRY_TO_LANGUAGE = {
  // English-speaking countries
  'US': 'en', 'GB': 'en', 'AU': 'en', 'CA': 'en', 'NZ': 'en', 'IE': 'en', 'ZA': 'en',
  // Italian
  'IT': 'it', 'SM': 'it', 'VA': 'it', 'CH': 'it', // Switzerland has Italian too
  // Spanish
  'ES': 'es', 'MX': 'es', 'AR': 'es', 'CO': 'es', 'PE': 'es', 'VE': 'es', 'CL': 'es', 
  'EC': 'es', 'GT': 'es', 'CU': 'es', 'BO': 'es', 'DO': 'es', 'HN': 'es', 'PY': 'es',
  'SV': 'es', 'NI': 'es', 'CR': 'es', 'PA': 'es', 'UY': 'es', 'PR': 'es',
  // Portuguese
  'BR': 'pt', 'PT': 'pt', 'AO': 'pt', 'MZ': 'pt',
  // German
  'DE': 'de', 'AT': 'de', 'LI': 'de',
  // French
  'FR': 'fr', 'BE': 'fr', 'LU': 'fr', 'MC': 'fr', 'SN': 'fr', 'CI': 'fr',
};

// Map country codes to default units
const COUNTRY_TO_UNITS = {
  // Imperial countries
  'US': { weight: 'lbs', height: 'ft', distance: 'mi', date: 'mdy', decimal: '.' },
  'LR': { weight: 'lbs', height: 'ft', distance: 'mi', date: 'mdy', decimal: '.' },
  'MM': { weight: 'lbs', height: 'ft', distance: 'mi', date: 'dmy', decimal: '.' },
  // UK uses a mix
  'GB': { weight: 'lbs', height: 'ft', distance: 'mi', date: 'dmy', decimal: '.' },
  // Metric countries (default)
  'default': { weight: 'kg', height: 'cm', distance: 'km', date: 'dmy', decimal: ',' },
};

const LanguageContext = createContext();

// Cache geo data in sessionStorage to avoid repeated API calls
const GEO_CACHE_KEY = 'app_geo_data';
const GEO_DETECTED_KEY = 'app_geo_detected';

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    // Try to get from localStorage first (user preference)
    const saved = localStorage.getItem('app_language');
    if (saved && translations[saved]) {
      return saved;
    }
    // Try to detect from browser as fallback
    const browserLang = navigator.language?.split('-')[0];
    if (browserLang && translations[browserLang]) {
      return browserLang;
    }
    return 'it'; // Default to Italian
  });

  const [geoData, setGeoData] = useState(() => {
    // Try to get cached geo data
    try {
      const cached = sessionStorage.getItem(GEO_CACHE_KEY);
      if (cached) return JSON.parse(cached);
    } catch (e) {}
    return null;
  });

  // Geo-detection: detect country from IP on EVERY app start (no
  // sessionStorage cache). The previous behaviour skipped detection if
  // a previous attempt was cached, which made VPN/country changes
  // invisible until the user cleared site data. The "manual override"
  // check now uses an explicit `app_language_manual` flag set only by
  // the user-driven setLanguage() call — not by geo auto-detect.
  useEffect(() => {
    const detectGeoLocation = async () => {
      const manualOverride = localStorage.getItem('app_language_manual') === '1';
      
      try {
        let countryCode = null;
        let countryName = null;
        
        // Try Cloudflare trace FIRST (always works, no CORS issues,
        // no rate limit, no token required). Reliable on iOS WKWebView.
        try {
          const response = await fetch('https://www.cloudflare.com/cdn-cgi/trace', { cache: 'no-store' });
          const text = await response.text();
          const lines = text.split('\n');
          for (const line of lines) {
            if (line.startsWith('loc=')) {
              countryCode = line.split('=')[1]?.toUpperCase();
              countryName = countryCode;
              break;
            }
          }
          console.log('[GeoDetect] Cloudflare trace country:', countryCode);
        } catch (e) {
          console.log('[GeoDetect] Cloudflare trace failed:', e?.message);
        }
        
        // Fallback to ipinfo.io if Cloudflare blocked
        if (!countryCode) {
          try {
            const response = await fetch('https://ipinfo.io/json');
            if (response.ok) {
              const data = await response.json();
              countryCode = data.country?.toUpperCase();
              countryName = data.country;
              console.log('[GeoDetect] ipinfo.io country:', countryCode);
            }
          } catch (e) {
            console.log('[GeoDetect] ipinfo.io failed:', e?.message);
          }
        }
        
        if (countryCode) {
          const geoInfo = {
            country: countryName,
            countryCode: countryCode,
            detectedAt: new Date().toISOString()
          };
          sessionStorage.setItem(GEO_CACHE_KEY, JSON.stringify(geoInfo));
          sessionStorage.setItem(GEO_DETECTED_KEY, 'true');
          setGeoData(geoInfo);
          
          // Auto-set language ONLY if the user has NOT manually picked
          // a language. Geo auto-detect from previous sessions does NOT
          // count as "manual" — only the explicit setLanguage() call does.
          if (!manualOverride) {
            const detectedLang = COUNTRY_TO_LANGUAGE[countryCode];
            if (detectedLang && translations[detectedLang]) {
              console.log(`[GeoDetect] Country: ${countryName} (${countryCode}), Setting language to: ${detectedLang}`);
              setLanguageState(detectedLang);
              localStorage.setItem('app_language', detectedLang);
              document.documentElement.lang = detectedLang;
              
              // Save to backend (cookie OR bearer)
              persistLanguageToBackend(detectedLang);
              
              // Dispatch event for UnitsContext to pick up
              window.dispatchEvent(new CustomEvent('geo-detected', { 
                detail: { countryCode, language: detectedLang } 
              }));
            }
          } else {
            console.log('[GeoDetect] Manual override active, not changing language');
          }
        }
      } catch (error) {
        console.log('[GeoDetect] Failed to detect location:', error.message);
        sessionStorage.setItem(GEO_DETECTED_KEY, 'failed');
      }
    };

    detectGeoLocation();
  }, []);

  // Listen for backend language sync (after login/auth check)
  useEffect(() => {
    const handleBackendSync = (e) => {
      const backendLang = e.detail?.language;
      const localLang = localStorage.getItem('app_language');
      if (!backendLang) return;
      if (translations[backendLang]) {
        // If the user has manually picked a language locally that differs from
        // the backend's, the local choice wins — push it back to the server
        // (this fixes the case where the previous PUT failed with 401 due to
        // missing Authorization header).
        if (localLang && localLang !== backendLang && translations[localLang]) {
          // Push local back to server using either auth method
          persistLanguageToBackend(localLang);
          return;  // keep local
        }
        setLanguageState(backendLang);
        localStorage.setItem('app_language', backendLang);
        document.documentElement.lang = backendLang;
      }
    };
    window.addEventListener('backend-language-sync', handleBackendSync);
    return () => window.removeEventListener('backend-language-sync', handleBackendSync);
  }, []);

  const setLanguage = useCallback(async (lang) => {
    if (translations[lang]) {
      setLanguageState(lang);
      localStorage.setItem('app_language', lang);
      // Mark this language choice as MANUAL so geo-detection won't
      // override it on the next app start.
      localStorage.setItem('app_language_manual', '1');
      document.documentElement.lang = lang;

      // Save language preference to backend.
      await persistLanguageToBackend(lang);
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // Translation function
  const t = useCallback((key, params = {}) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to Italian
        value = translations['it'];
        for (const k2 of keys) {
          if (value && typeof value === 'object' && k2 in value) {
            value = value[k2];
          } else {
            return key; // Return key if not found
          }
        }
        break;
      }
    }

    // Replace params like {{name}} with actual values
    if (typeof value === 'string' && Object.keys(params).length > 0) {
      return value.replace(/\{\{(\w+)\}\}/g, (_, paramKey) => params[paramKey] || '');
    }

    return typeof value === 'string' ? value : key;
  }, [language]);

  const value = {
    language,
    setLanguage,
    t,
    languages: LANGUAGES,
    currentLanguage: LANGUAGES.find(l => l.code === language) || LANGUAGES[0],
    geoData, // Expose geo data for other components
    getUnitsForCountry: (countryCode) => COUNTRY_TO_UNITS[countryCode] || COUNTRY_TO_UNITS['default']
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

// Shorthand hook for just the translation function
export function useTranslation() {
  const { t, language } = useLanguage();
  return { t, language };
}
